import type { PersonalizationState } from '../hooks/usePersonalization';

interface Props {
  state: PersonalizationState;
}

// 트래킹 ID 가 없거나(idle) 서버 호출 실패(error) 또는 응답이 식별 정보를
// 누락한 경우, 사용자 식별 정보 노출 없이 보편적으로 안전한 기본 메시지 사용.
const FALLBACK_TITLE = '보호자님, 알림이 도착했어요';
const FALLBACK_MESSAGE = '우리엔 앱에서 확인해주세요.';

function FallbackMessage() {
  return (
    <article className="w-full rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70 sm:p-7">
      <h2 className="text-xl font-semibold leading-snug text-slate-900 sm:text-[22px]">
        {FALLBACK_TITLE}
      </h2>
      <p className="mt-3 text-[15px] leading-relaxed text-slate-600 sm:text-base">
        {FALLBACK_MESSAGE}
      </p>
    </article>
  );
}

export function PersonalizedMessage({ state }: Props) {
  if (state.status === 'loading') {
    return (
      <article
        className="w-full rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70"
        aria-busy="true"
      >
        <div className="h-6 w-3/4 animate-pulse rounded bg-slate-200" />
        <div className="mt-3 h-4 w-2/3 animate-pulse rounded bg-slate-100" />
        <div className="mt-2 h-4 w-1/2 animate-pulse rounded bg-slate-100" />
      </article>
    );
  }

  if (state.status === 'success') {
    const { guardianName, petName, message } = state.data;

    // 서버가 식별 정보를 일부라도 누락하면 fallback 으로 빠진다.
    // 이로써 "홍길동님, undefined 에게..." 같은 깨진 표시를 방지.
    if (!guardianName || !petName || !message) {
      return <FallbackMessage />;
    }

    return (
      <article className="w-full rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70 sm:p-7">
        <h2 className="text-xl font-semibold leading-snug text-slate-900 sm:text-[22px]">
          {`${guardianName}님, ${petName}에게 알림이 도착했어요`}
        </h2>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-600 sm:text-base">
          {message}
        </p>
      </article>
    );
  }

  // idle (tracking_id 부재) | error (네트워크/서버 실패)
  return <FallbackMessage />;
}
